import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiTokenRequest, msalInstance } from './msalConfig';

const baseUrl = (import.meta.env.VITE_API_BASE_URL as string) ?? '';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function getAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new ApiError(401, 'no_account', 'No active MSAL account');

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
      // Redirect interrupts execution; this throw keeps types clean.
      throw new ApiError(401, 'interaction_required', 'Redirect to sign in');
    }
    throw err;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${baseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let payload: { error?: string; message?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // empty body is fine
    }
    throw new ApiError(res.status, payload.error ?? 'http_error', payload.message ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface UploadResult {
  container: string;
  path: string;
  url: string;
  size: number;
  contentType: string;
}

/**
 * Multipart POST to /api/uploads/:container. The browser sets the Content-Type
 * header (with boundary) automatically when the body is a FormData.
 */
async function upload(
  container: string,
  file: File | Blob,
  options: { path?: string; filename?: string } = {},
): Promise<UploadResult> {
  const token = await getAccessToken();
  const form = new FormData();
  if (options.path) form.append('path', options.path);
  const filename = options.filename
    ?? (file instanceof File ? file.name : 'upload.bin');
  form.append('file', file, filename);

  const res = await fetch(`${baseUrl}/api/uploads/${encodeURIComponent(container)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  return handleResponse<UploadResult>(res);
}

export const apiClient = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload,
};
